import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "./auth";
import { type FeatureFlags, isFinancingAvailable, useFeatures } from "./features";

const STORAGE_KEY_PREFIX = "octa-feature-awareness";

type FeatureRoute = {
  featureKey: keyof FeatureFlags;
  route: string;
  icon: string;
  labelKey: string;
  descriptionKey: string;
};

export const FEATURE_ROUTE_MAP: FeatureRoute[] = [
  {
    featureKey: "automations",
    route: "/automations",
    icon: "Zap",
    labelKey: "nav.automations",
    descriptionKey: "featureDiscovery.desc.automations",
  },
  {
    featureKey: "financing",
    route: "/financing",
    icon: "Landmark",
    labelKey: "nav.financing",
    descriptionKey: "featureDiscovery.desc.financing",
  },
  {
    featureKey: "communications",
    route: "/communications",
    icon: "Mail",
    labelKey: "nav.communications",
    descriptionKey: "featureDiscovery.desc.communications",
  },
  {
    featureKey: "reconciliation",
    route: "/settings?section=integrations&sub=sync",
    icon: "CheckCircle2",
    labelKey: "featureDiscovery.label.reconciliation",
    descriptionKey: "featureDiscovery.desc.reconciliation",
  },
  {
    featureKey: "aiReports",
    route: "/reports",
    icon: "BarChart3",
    labelKey: "nav.reports",
    descriptionKey: "featureDiscovery.desc.aiReports",
  },
  {
    featureKey: "contractToCash",
    route: "/ar/contracts",
    icon: "FileText",
    labelKey: "featureDiscovery.label.contractToCash",
    descriptionKey: "featureDiscovery.desc.contractToCash",
  },
  {
    featureKey: "directDebit",
    route: "/ar/direct-debit",
    icon: "Landmark",
    labelKey: "featureDiscovery.label.directDebit",
    descriptionKey: "featureDiscovery.desc.directDebit",
  },
  {
    featureKey: "arInstruments",
    route: "/ar",
    icon: "Receipt",
    labelKey: "featureDiscovery.label.arInstruments",
    descriptionKey: "featureDiscovery.desc.arInstruments",
  },
  {
    featureKey: "apMatching",
    route: "/ap",
    icon: "Layers",
    labelKey: "featureDiscovery.label.apMatching",
    descriptionKey: "featureDiscovery.desc.apMatching",
  },
];

export const WIDGET_FEATURE_MAP: Record<string, keyof FeatureFlags> = {
  financing: "financing",
  direct_debit: "directDebit",
  payment_plans: "arInstruments",
  currency_exposure: "multiEntity",
};

function getStorageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}-${userId}`;
}

function loadSeenFeatures(userId: string): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function saveSeenFeatures(userId: string, seen: Record<string, boolean>) {
  try {
    localStorage.setItem(getStorageKey(userId), JSON.stringify(seen));
  } catch {}
}

function loadPreviousTier(userId: string): string | null {
  try {
    return localStorage.getItem(`${STORAGE_KEY_PREFIX}-tier-${userId}`);
  } catch {
    return null;
  }
}

function savePreviousTier(userId: string, tier: string) {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}-tier-${userId}`, tier);
  } catch {}
}

export function useFeatureAwareness() {
  const { user } = useAuth();
  const features = useFeatures();
  const [location] = useLocation();
  const userId = user?.id || "unknown";

  const [seenFeatures, setSeenFeatures] = useState<Record<string, boolean>>(() => loadSeenFeatures(userId));

  useEffect(() => {
    setSeenFeatures(loadSeenFeatures(userId));
  }, [userId]);

  useEffect(() => {
    const currentTier = features.tier;
    const previousTier = loadPreviousTier(userId);

    if (previousTier && previousTier !== currentTier) {
      const seen = loadSeenFeatures(userId);
      const previouslyUnavailable = FEATURE_ROUTE_MAP
        .filter((fr) => features[fr.featureKey] === true)
        .map((fr) => fr.featureKey as string)
        .filter((key) => !(key in seen));

      if (previouslyUnavailable.length > 0) {
        saveSeenFeatures(userId, seen);
        setSeenFeatures(seen);
      }
    }

    savePreviousTier(userId, currentTier);
  }, [features.tier, userId]);

  useEffect(() => {
    const matchedFeature = FEATURE_ROUTE_MAP.find((fr) => {
      if (fr.route.includes("?")) {
        return location.startsWith(fr.route.split("?")[0] ?? fr.route);
      }
      return location.startsWith(fr.route);
    });

    if (matchedFeature && features[matchedFeature.featureKey] === true) {
      const key = matchedFeature.featureKey as string;
      if (!seenFeatures[key]) {
        const updated = { ...seenFeatures, [key]: true };
        setSeenFeatures(updated);
        saveSeenFeatures(userId, updated);
      }
    }
  }, [location, features, userId]);

  const markSeen = useCallback((featureKey: string) => {
    setSeenFeatures((prev) => {
      const updated = { ...prev, [featureKey]: true };
      saveSeenFeatures(userId, updated);
      return updated;
    });
  }, [userId]);

  const hasNew = useCallback((featureKey: string): boolean => {
    const fk = featureKey as keyof FeatureFlags;
    if (features[fk] !== true) return false;
    if (fk === "financing" && !isFinancingAvailable(features)) return false;
    return !seenFeatures[featureKey];
  }, [features, seenFeatures]);

  const newFeatures = useMemo(() => {
    return FEATURE_ROUTE_MAP.filter((fr) => hasNew(fr.featureKey as string));
  }, [hasNew]);

  const newNavFeatures = useMemo(() => {
    const navFeatureKeys = [
      "automations",
      "financing",
      "communications",
      "reconciliation",
      "aiReports",
      "contractToCash",
      "directDebit",
    ];
    return navFeatureKeys.filter((key) => hasNew(key));
  }, [hasNew]);

  const newWidgets = useMemo(() => {
    return Object.entries(WIDGET_FEATURE_MAP)
      .filter(([_, featureKey]) => hasNew(featureKey as string))
      .map(([widgetId]) => widgetId);
  }, [hasNew]);

  const totalNewCount = newFeatures.length;

  return {
    newFeatures,
    newNavFeatures,
    newWidgets,
    markSeen,
    hasNew,
    totalNewCount,
    seenFeatures,
  };
}
